import Phaser from "phaser";
import { DEPTH } from "./depthLayers.js";

export interface AimTargetMarkerVisual {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly brickTint: number;
  readonly width: number;
  readonly height: number;
}

const DEFAULT_BRICK_AREA = 48 * 26;
const MIN_MARKER_SCALE = 0.75;
const MAX_MARKER_SCALE = 1.3;
const LOCK_ON_SCALE_MULT = 1.25;
const MARKER_IDLE_ALPHA = 0.78;
const MARKER_LOCK_ON_ALPHA = 0.56;
const RGB_MASK = 16_777_215;
const RGB_CHANNEL_MAX = 255;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function hueForRgb(r: number, g: number, b: number): number {
  const rn = r / RGB_CHANNEL_MAX;
  const gn = g / RGB_CHANNEL_MAX;
  const bn = b / RGB_CHANNEL_MAX;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  if (delta === 0) return 0;
  if (max === rn) return ((gn - bn) / delta + (gn < bn ? 6 : 0)) * 60;
  if (max === gn) return ((bn - rn) / delta + 2) * 60;
  return ((rn - gn) / delta + 4) * 60;
}

function hsvToTint(hueDeg: number, saturation: number, value: number): number {
  const h = ((hueDeg % 360) + 360) % 360;
  const c = value * saturation;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = value - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const ri = Math.round((r + m) * RGB_CHANNEL_MAX);
  const gi = Math.round((g + m) * RGB_CHANNEL_MAX);
  const bi = Math.round((b + m) * RGB_CHANNEL_MAX);
  return (ri << 16) | (gi << 8) | bi;
}

export function targetMarkerTintForBrick(brickTint: number): number {
  const tint = brickTint & RGB_MASK;
  const r = (tint >> 16) & RGB_CHANNEL_MAX;
  const g = (tint >> 8) & RGB_CHANNEL_MAX;
  const b = tint & RGB_CHANNEL_MAX;
  return hsvToTint(hueForRgb(r, g, b) + 180, 1, 1);
}

export function targetMarkerScaleForBrick(width: number, height: number): number {
  const area = Math.max(1, width) * Math.max(1, height);
  return clamp(Math.sqrt(area / DEFAULT_BRICK_AREA), MIN_MARKER_SCALE, MAX_MARKER_SCALE);
}

/**
 * 조준 타겟 마커 — 발사대가 조준 중인 블럭 위에 표시되는 단일 시각 컴포넌트(풀링 1개).
 *
 * 타겟이 바뀌면(락온) scale out→in + 작은 회전 애니메이션을 재생한다. 마커 텍스처(`target_marker`)는
 * 4코너 브래킷 + 상단 방향 인디케이터의 비대칭 디자인이라 회전이 눈에 띈다. 시각·tween·마스크·표시
 * 상태를 여기 캡슐화하고, BattlefieldRunner(셸)는 매 프레임 update(visual)로 구동만 한다 — Humble View.
 */
export class AimTargetMarker {
  private readonly image: Phaser.GameObjects.Image;
  /** 게임뷰 마스크 1회 적용 플래그 — 마스크는 buildUi 이후 생성되므로 첫 표시 시점에 적용. */
  private masked = false;
  /** 현재 부착된 타겟 id — 바뀔 때만 락온 애니를 재생(매 프레임 재생 방지). */
  private targetId: string | null = null;

  /**
   * @param scene - 마커가 속한 씬(tween·add 소속).
   * @param applyMask - 첫 표시 시 게임뷰 마스크를 적용하는 콜백(마스크가 늦게 생성되므로 주입).
   */
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly applyMask: (img: Phaser.GameObjects.Image) => void,
  ) {
    this.image = scene.add
      .image(0, 0, "target_marker")
      .setDepth(DEPTH.targetMarker)
      .setVisible(false);
  }

  /**
   * 현재 조준 타겟의 시각 정보로 갱신한다. visual이 null이면(조준 대상 없음) 마커를 숨긴다.
   * id가 직전과 달라지면(락온) scale out→in + 회전 애니메이션을 재생한다.
   */
  update(visual: AimTargetMarkerVisual | null): void {
    const m = this.image;
    if (!visual) {
      if (m.visible) {
        this.scene.tweens.killTweensOf(m);
        m.setVisible(false);
      }
      this.targetId = null;
      return;
    }
    if (!this.masked) {
      this.applyMask(m);
      this.masked = true;
    }
    const targetScale = targetMarkerScaleForBrick(visual.width, visual.height);
    m.setPosition(visual.x, visual.y).setTint(targetMarkerTintForBrick(visual.brickTint));
    if (!m.visible) m.setVisible(true);
    if (visual.id !== this.targetId) {
      this.targetId = visual.id;
      this.playLockOn(targetScale);
    } else if (!this.scene.tweens.isTweening(m)) {
      m.setScale(targetScale).setAlpha(MARKER_IDLE_ALPHA);
    }
  }

  /** 락온 연출 — 큰 스케일·기울기·반투명에서 제자리로 튀어 들어온다(Back.easeOut 오버슈트 = scale out-in 펀치 + 회전). */
  private playLockOn(targetScale: number): void {
    const m = this.image;
    this.scene.tweens.killTweensOf(m);
    m.setScale(targetScale * LOCK_ON_SCALE_MULT).setAngle(-40).setAlpha(MARKER_LOCK_ON_ALPHA);
    this.scene.tweens.add({
      targets: m,
      scale: targetScale,
      angle: 0,
      alpha: MARKER_IDLE_ALPHA,
      duration: 240,
      ease: "Back.easeOut",
    });
  }
}
